import { Injectable } from '@angular/core';
import { ActiveToast, IndividualConfig, ProgressAnimationType, ToastrService } from 'ngx-toastr';
import { DashboardCacheService } from 'src/app/modules/devices/dashboard/services/dashboard-cache.service';

@Injectable({
    providedIn: 'root'
})
export class ToastService {

    private simpleMessage: ActiveToast<any>;
    private locationMessages = new Map<number, ActiveToast<any>>();
    private areaMessages = new Map<number, ActiveToast<any>>();
    private deviceMessages = new Map<number, ActiveToast<any>>();

    private readonly defaultSimpleTimeout = 4000;
    private readonly defaultConfig = {
        positionClass: 'toast-bottom-full-width',
        tapToDismiss: false,
        closeButton: true,
        extendedTimeOut: 180 * 1000
    };

    constructor(
        private toastr: ToastrService,
        private dashboardCacheService: DashboardCacheService
    ) {
    }

    public showSimple(message: string, title?: string) {
        this.showMessage(message, title, this.defaultSimpleTimeout, false, 'decreasing', false);
    }

    public showTopError(message: string) {
        if (this.simpleMessage) {
            this.toastr.remove(this.simpleMessage.toastId);
        }
        this.simpleMessage = this.toastr.error(message, undefined, {
            timeOut: this.defaultSimpleTimeout,
            positionClass: 'toast-top-full-width'
        });
    }

    public showLocationMessage(locationId: number, message: string, timeOut?: number) {
        const title = this.getLocationName(locationId);
        this.locationMessages.set(locationId, this.showMessage(message, title, timeOut));
    }

    public showLocationMessagePending(locationId: number, message: string, timeOut?: number, showProgressBar?: boolean) {
        const title = this.getLocationName(locationId);
        this.locationMessages.set(locationId, this.showMessage(message, title, timeOut, showProgressBar, 'increasing'));
    }

    public showLocationMessageSuccess(locationId: number, message: string, timeOut?: number) {
        const title = this.getLocationName(locationId);
        this.locationMessages.set(locationId, this.showSuccess(message, title, timeOut));
    }

    public showLocationMessageError(locationId: number, message: string, timeOut?: number) {
        const title = this.getLocationName(locationId);
        this.locationMessages.set(locationId, this.showError(message, title, timeOut));
    }

    public removeLocationMessage(locationId: number) {
        const locationMessage = this.locationMessages.get(locationId);
        if (locationMessage) {
            this.toastr.remove(locationMessage.toastId);
        }
    }

    public showAreaMessage(locationId: number, areaId: number, message: string, timeOut?: number) {
        const title = this.getAreaTitle(locationId, areaId);
        this.areaMessages.set(areaId, this.showMessage(message, title, timeOut));
    }

    public removeAreaMessage(areaId: number) {
        const areaMessage = this.areaMessages.get(areaId);
        if (areaMessage) {
            this.toastr.remove(areaMessage.toastId);
        }
    }

    public showDeviceMessage(locationId: number, areaId: number, deviceId: number, message: string, timeOut?: number, showProgressBar?: boolean) {
        const title = this.getAreaTitle(locationId, areaId);
        this.deviceMessages.set(deviceId, this.showMessage(message, title, timeOut, showProgressBar, 'increasing'));
    }

    public showDeviceSuccess(locationId: number, areaId: number, deviceId: number, message: string, timeOut?: number) {
        const title = this.getAreaTitle(locationId, areaId);
        this.deviceMessages.set(deviceId, this.showSuccess(message, title, timeOut));
    }

    public showDeviceWarning(locationId: number, areaId: number, deviceId: number, message: string, timeOut?: number) {
        const title = this.getAreaTitle(locationId, areaId);
        this.deviceMessages.set(deviceId, this.showWarning(message, title, timeOut));
    }

    public showDeviceError(locationId: number, areaId: number, deviceId: number, message: string, timeOut?: number) {
        const title = this.getAreaTitle(locationId, areaId);
        this.deviceMessages.set(deviceId, this.showError(message, title, timeOut));
    }

    public removeDeviceMessage(deviceId: number) {
        const deviceMessage = this.deviceMessages.get(deviceId);
        if (deviceMessage) {
            this.toastr.remove(deviceMessage.toastId);
        }
    }

    private getAreaTitle(locationId: number, areaId: number): string {
        return `${this.getLocationName(locationId)} / ${this.getAreaName(locationId, areaId)}`;
    }

    private getLocationName(locationId: number) {
        const location = this.dashboardCacheService.getCachedDashboard(locationId);
        return location ? location.locationName : 'Unknown location name';
    }

    private getAreaName(locationId: number, areaId: number) {
        const location = this.dashboardCacheService.getCachedDashboard(locationId);
        return location ? location.areasById.get(areaId)?.areaTag : 'Unknown area name';
    }

    private showMessage(message: string, title: string,
                        timeOut?: number,
                        showProgressBar?: boolean,
                        progressAnimation?: ProgressAnimationType,
                        showCloseButton = true): ActiveToast<any> {
        const config = this.getDefaultConfig(timeOut, showProgressBar, progressAnimation);
        config.closeButton = showCloseButton;
        return this.toastr.show(message, title, config);
    }

    private showSuccess(message: string, title: string, timeOut?: number): ActiveToast<any> {
        return this.toastr.success(message, title, this.getDefaultConfig(timeOut));
    }

    private showWarning(message: string, title: string, timeOut?: number): ActiveToast<any> {
        return this.toastr.warning(message, title, this.getDefaultConfig(timeOut));
    }

    private showError(message: string, title: string, timeOut?: number): ActiveToast<any> {
        return this.toastr.error(message, title, this.getDefaultConfig(timeOut));
    }

    private getDefaultConfig(timeOut?: number, showProgressBar?: boolean, progressAnimation?: ProgressAnimationType): Partial<IndividualConfig> {
        return {
            ...this.defaultConfig,
            timeOut,
            progressBar: showProgressBar,
            progressAnimation: progressAnimation || 'decreasing'
        };
    }

    public clearSnackbar() {
        this.toastr.clear();
    }
}
